/**
 * DanismanOgrenciDetay.jsx  —  /danisman/ogrenci/:userId
 *
 * Danışmana özel SADE öğrenci detay ekranı (bkz. proje-yoneticisi görev
 * talimatı, 2026-08-14 — İş B). `/admin/students/:userId`
 * (AdminStudentDetailPage — admin-only tam yönetici görünümü, 9 sekme:
 * KVKK, güvenlik/şifre, sertifikalar, muhasebe vb.) BİLİNÇLİ OLARAK
 * KULLANILMAZ; danışman panosu artık admin öğrenci sayfasına GİRMİYOR —
 * aynı karar `GET /danisman/ogrencilerim/{user_id}` docstring'inde de var
 * (bkz. backend/routes/danisman_cockpit.py).
 *
 * Bu ekranda BULUNMAYAN yönetici işlevleri (bilinçli olarak konulmadı):
 *   - KVKK / silme / anonimleştirme
 *   - Rol değiştirme, global ayarlar
 *   - Başka danışmanın verisi (backend zaten sahiplik kısıtlı, 403 döner)
 *   - Şifre sıfırlama, öğrenci no değiştirme
 *   - Satış personeli seçimi/override'ı (danışman rolünde backend body'den
 *     gelen satis_personel_id'yi zaten YOK SAYIYOR — frontend'den de
 *     gönderilmiyor, bkz. DersEkleKart)
 *   - SMS onay kodu gönderme/doğrulama, referans kodu sorgulama, sertifika/
 *     sınav giriş tipi seçimi, "ek ders sonra seçilecek" hakkı
 *
 * FAZ 1 düzeltme turu — madde F2 (2026-09-25): danışman artık taksit
 * tablosunu elle düzenleyebilir (bkz. DersEkleKart'taki
 * `components/sales/InstallmentPreview.jsx` `editable` kullanımı) —
 * dokunmazsa backend eşit böler (öncekiyle AYNI), dokunursa
 * `installment_plan` gönderilir; role özel bir kısıtlama YOKTUR
 * (routes/admin.py::admin_enroll_student_courses F2 kararı).
 *
 * Endpoint'ler:
 *   GET  /danisman/ogrencilerim/{user_id}          — detay (salt okuma, sahiplik kısıtlı, 403 yetkisiz_ogrenci)
 *   GET  /courses?limit=200                        — kurs kataloğu (public)
 *   POST /admin/students/{user_id}/enroll-courses  — ders ekleme / satış (sahiplik kısıtlı,
 *        payload AdminStudentSalePage.jsx'ten (routes/admin.py::admin_enroll_student_courses
 *        sözleşmesine göre) birebir alınmıştır — courses[], payment_method,
 *        installments+first_due_date VEYA installment_plan, payment_notes)
 *
 * Admin/superadmin de bu sayfaya erişebilir (RoleRoute) — sade görünüm
 * onlar için de kullanılabilir; tam yönetici görünümüne erişimleri
 * PageHeader actions'taki "Tam yönetici görünümü" linkiyle korunur.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import AdminLayout from '../components/admin/AdminLayout';
import PageHeader from '../components/admin/PageHeader';
import ErrorBoundary, { BlockErrorFallback } from '../components/ErrorBoundary';
import { ConfirmDialog } from '../components/admin';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage, getApiErrorCode } from '../utils/apiError';
import { fmtMoney } from '../utils/money';
import { rootBreadcrumb } from '../utils/adminBreadcrumbs';
import { Skeleton } from '../components/ui/skeleton';
import { useAccountingBanks } from '../api/queries/accounting';
// FAZ 1 satış sadeleştirme (2026-09-25) — satış anında POS hesabı tahsilat
// hesabı olarak KABUL EDİLMEZ, yalnızca kasa/banka listelenir.
import { isPosAccountType } from '../lib/posNetAmount';
import PaymentPlanFields from '../components/sales/PaymentPlanFields';
import {
  TelefonHucre,
  INPUT_CLS,
  LBL_CLS,
  fmtDate,
  fmtDateTime,
} from '../components/crm/DanismanCockpitShared';
import {
  User,
  Mail,
  MapPin,
  Calendar,
  BadgeCheck,
  BookOpen,
  ReceiptText,
  MessageSquare,
  ShoppingCart,
  Search,
  Plus,
  Trash2,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Loader2,
  ExternalLink,
  ShieldAlert,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Pencil,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NOTE_CLAMP_THRESHOLD = 220;

// ─── Ortak parçalar ─────────────────────────────────────────────────────────

function Card({ children, className = '' }) {
  return (
    <div
      className={`bg-white p-4 sm:p-5 ${className}`}
      style={{ borderRadius: 14, border: '1px solid var(--crm-card-border)' }}
    >
      {children}
    </div>
  );
}

function CardTitle({ icon, children }) {
  return (
    <h2 className="font-bold text-base flex items-center gap-2 mb-3" style={{ color: 'var(--crm-navy-text)' }}>
      {icon} {children}
    </h2>
  );
}

function ErrorRetry({ onRetry, tr }) {
  return (
    <div
      className="p-3 rounded-xl flex items-center justify-between gap-3 flex-wrap"
      style={{ backgroundColor: 'rgba(160,61,44,0.06)', border: '1px solid rgba(160,61,44,0.25)' }}
    >
      <span className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--crm-danger)' }}>
        <AlertTriangle size={16} /> {tr('Bağlantı koptu — tekrar dene', 'Connection lost — try again')}
      </span>
      <button
        onClick={onRetry}
        className="px-3 py-1.5 rounded-lg text-xs font-bold text-white flex items-center gap-1 flex-shrink-0"
        style={{ backgroundColor: 'var(--crm-danger)' }}
      >
        <RefreshCw size={13} /> {tr('Tekrar Dene', 'Retry')}
      </button>
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <label className={LBL_CLS}>
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
      {error && (
        <p className="text-[10px] text-rose-600 mt-1 font-bold flex items-center gap-1">
          <AlertCircle size={11} />{error}
        </p>
      )}
    </div>
  );
}

function KursDurumBadge({ durum, tr }) {
  const map = {
    active: { cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: tr('Aktif', 'Active') },
    paused: { cls: 'bg-amber-100 text-amber-700 border-amber-200', label: tr('Duraklatıldı', 'Paused') },
    completed: { cls: 'bg-blue-100 text-blue-700 border-blue-200', label: tr('Tamamlandı', 'Completed') },
    cancelled: { cls: 'bg-rose-100 text-rose-700 border-rose-200', label: tr('İptal', 'Cancelled') },
  };
  const { cls, label } = map[durum] || { cls: 'bg-gray-100 text-gray-600 border-gray-200', label: durum || '-' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border ${cls}`}>{label}</span>;
}

function SatisDurumBadge({ durum, tr }) {
  const map = {
    issued: { cls: 'bg-gray-100 text-gray-600 border-gray-200', label: tr('Kesildi', 'Issued') },
    partially_paid: { cls: 'bg-amber-100 text-amber-700 border-amber-200', label: tr('Kısmi Ödendi', 'Partially Paid') },
    paid: { cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', label: tr('Ödendi', 'Paid') },
    cancelled: { cls: 'bg-rose-100 text-rose-700 border-rose-200', label: tr('İptal', 'Cancelled') },
  };
  const { cls, label } = map[durum] || { cls: 'bg-gray-100 text-gray-600 border-gray-200', label: durum || '-' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border ${cls}`}>{label}</span>;
}

// ─── Bölüm 1 — Öğrenci Bilgileri (salt okuma) ────────────────────────────────

function OgrenciBilgileriKart({ ogrenci, tr }) {
  return (
    <Card>
      <CardTitle icon={<User size={16} style={{ color: 'var(--crm-navy)' }} />}>
        {tr('Öğrenci Bilgileri', 'Student Info')}
      </CardTitle>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <p className="font-display text-lg font-bold" style={{ color: 'var(--crm-navy-text)' }}>
            {ogrenci.ad_soyad || '-'}
          </p>
          <p className="text-xs font-semibold" style={{ color: 'var(--crm-muted)' }}>
            {tr('Öğrenci No', 'Student No')}: {ogrenci.ogrenci_no || '-'}
          </p>
        </div>
        <span
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border flex-shrink-0 ${
            ogrenci.dogrulandi
              ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
              : 'bg-gray-100 text-gray-500 border-gray-200'
          }`}
        >
          <BadgeCheck size={13} /> {ogrenci.dogrulandi ? tr('Doğrulandı', 'Verified') : tr('Doğrulanmadı', 'Not verified')}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div className="flex items-center gap-2">
          <Mail size={14} className="flex-shrink-0" style={{ color: 'var(--crm-muted)' }} />
          <span className="truncate">{ogrenci.email || '-'}</span>
        </div>
        <TelefonHucre telefon={ogrenci.telefon} tr={tr} />
        <div className="flex items-center gap-2">
          <MapPin size={14} className="flex-shrink-0" style={{ color: 'var(--crm-muted)' }} />
          <span>{ogrenci.sehir || '-'}</span>
        </div>
        <div className="flex items-center gap-2">
          <Calendar size={14} className="flex-shrink-0" style={{ color: 'var(--crm-muted)' }} />
          <span>{tr('Kayıt', 'Registered')}: {fmtDate(ogrenci.kayit_tarihi)}</span>
        </div>
      </div>
    </Card>
  );
}

// ─── Bölüm 2 — Kurslar ────────────────────────────────────────────────────────

function KurslarKart({ kurslar, tr }) {
  return (
    <Card>
      <CardTitle icon={<BookOpen size={16} style={{ color: 'var(--crm-navy)' }} />}>
        {tr('Kurslar', 'Courses')}
      </CardTitle>
      {kurslar.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--crm-muted)' }}>
          {tr('Henüz ders eklenmemiş.', 'No courses added yet.')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b" style={{ borderColor: 'var(--crm-card-border)' }}>
                <th scope="col" className="text-left py-2 pr-3 font-bold text-xs uppercase" style={{ color: 'var(--crm-muted-2)' }}>
                  {tr('Kurs Adı', 'Course')}
                </th>
                <th scope="col" className="text-left py-2 pr-3 font-bold text-xs uppercase" style={{ color: 'var(--crm-muted-2)' }}>
                  {tr('Durum', 'Status')}
                </th>
                <th scope="col" className="text-left py-2 font-bold text-xs uppercase" style={{ color: 'var(--crm-muted-2)' }}>
                  {tr('Kayıt Tarihi', 'Registration Date')}
                </th>
              </tr>
            </thead>
            <tbody>
              {kurslar.map((k, i) => (
                <tr key={`${k.course_id}-${i}`} className="border-b last:border-0" style={{ borderColor: 'var(--crm-card-border)' }}>
                  <td className="py-2 pr-3 font-semibold">{k.kurs_adi}</td>
                  <td className="py-2 pr-3"><KursDurumBadge durum={k.durum} tr={tr} /></td>
                  <td className="py-2">{fmtDate(k.kayit_tarihi)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ─── Bölüm 3 — Ders Ekle / Satış ─────────────────────────────────────────────
//
// Payload sözleşmesi AdminStudentSalePage.jsx'in POST /admin/students/{id}/
// enroll-courses çağrısından (backend routes/admin.py::admin_enroll_student_
// courses) birebir alınmıştır. Sadeleştirme: satış personeli seçimi (backend
// danışman rolünde zaten yok sayıyor), SMS onay kodu, referans kodu,
// sertifika/sınav giriş tipi, "ek ders sonra seçilecek" ve manuel taksit
// satırı düzenleme YOK — yalnızca taksit sayısı + ilk vade tarihi (backend
// eşit böler, bkz. services/sales_service.py::create_sale).

// `export` yalnızca test izolasyonu içindir (bkz. __tests__/sales/
// DersEkleKartCertificateFee.test.jsx) — sayfa dışındaki hiçbir kod bunu
// import ETMEMELİ, tek route giriş noktası hâlâ aşağıdaki `export default`.
export function DersEkleKart({ userId, authHeaders, lang, tr, onSuccess, isAdmin, existingKurslar }) {
  const toast = useToast();
  // `tr(trText, enText)` — bu bileşenin (ve genel olarak bu sayfanın) mevcut
  // deseni, ayrı i18n key yerine literal metin çifti taşır. Yeni "sales.*"
  // metinleri (LanguageContext.js) için `t()` AYRICA çekilir — iki desen bir
  // arada: eski metinler `tr()`, FAZ 1'de eklenen ortak metinler `t()`.
  const { t } = useLanguage();

  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [coursesError, setCoursesError] = useState(false);
  const [search, setSearch] = useState('');

  const [cartItems, setCartItems] = useState([]);
  const [notes, setNotes] = useState('');
  // FAZ 1 karma ödeme turu (2026-09-25) — Ödeme bölümü (Peşinat + Kalan)
  // artık paylaşılan components/sales/PaymentPlanFields.jsx tarafından
  // yönetilir; bu bileşen kendi hesapladığı payload parçalarını (down_payment,
  // installments/first_due_date VEYA installment_plan, payment_method) ve
  // doğrulama durumunu (`meta.isValid`) `onChange` ile bildirir.
  const [paymentPlanFields, setPaymentPlanFields] = useState({});
  const [paymentPlanValid, setPaymentPlanValid] = useState(false);
  const [paymentPlanResetKey, setPaymentPlanResetKey] = useState(0);
  // FAZ 1 satış sadeleştirme (2026-09-25) — satış anında GERÇEKLEŞEN tahsilat
  // için zorunlu kasa/banka hesabı. SADECE admin bu ekrandan peşin nakit/havale
  // peşinat tahsilatı girebilir: danışman rolünde backend down_payment'ı
  // sunucu tarafında tarihli bir havale borç satırına çevirir (bkz.
  // PaymentPlanFields.jsx dosya başı yorumu) — bank_id o rolde hiç okunmaz/
  // istenmez. GET /admin/banks da zaten require_admin (danışman 403 alır) —
  // bu yüzden `enabled: isAdmin`.
  const { data: allBanks = [], isLoading: banksLoading } = useAccountingBanks({ enabled: isAdmin });
  const collectionBanks = allBanks.filter((b) => !isPosAccountType(b.account_type));
  // Sertifika turu — FAZ 1 karma ödeme turu (2026-09-25, madde 3): kaynak
  // artık ücretli katalog (`GET /admin/certificates/types`, `certificate_types`
  // koleksiyonu, `exam_fee` alanlı) — eskiden Tanımlamalar (`sertifika_tipi`,
  // ücretsiz/serbest metin) kullanılıyordu. ANA egitimde hala ZORUNLU (25
  // Agustos 2026, Recep kararı).
  const [certTypes, setCertTypes] = useState([]);
  const [certTypesError, setCertTypesError] = useState(false);
  const [certificateTypeId, setCertificateTypeId] = useState('');
  // Kutu anlamı: İŞARETLİ = kurs fiyatı sertifika ücretini ZATEN içeriyor,
  // bu yüzden ücret toplama EKLENMEZ. İŞARETSİZ = ücret toplama eklenir.
  // Varsayılan işaretli (fiyat sertifikayı içerir, eklenmez) — Recep kararı
  // (bkz. görev notu, 2026-09-28 "sertifika ücreti dahil kutusu" düzeltmesi).
  const [certificateFeeIncluded, setCertificateFeeIncluded] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fetchCourses = useCallback(async () => {
    setCoursesLoading(true);
    setCoursesError(false);
    try {
      const r = await axios.get(`${API}/courses?limit=200`);
      setCourses(r.data.courses || []);
    } catch {
      setCoursesError(true);
    } finally {
      setCoursesLoading(false);
    }
  }, []);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  // FAZ 1 karma ödeme turu (2026-09-25, madde 3) — ücretli katalog; danışmana
  // da açık (require_danisman, bkz. routes/certificates_admin.py dosya başı
  // yorumu). Liste boşsa backend varsayılan 4 türü seed eder, boş dönmez.
  const fetchCertTypes = useCallback(async () => {
    setCertTypesError(false);
    try {
      const r = await axios.get(`${API}/admin/certificates/types`, { headers: authHeaders() });
      setCertTypes(r.data?.certificates || []);
    } catch {
      // Liste cekilemezse SESSIZ GECMIYORUZ: ANA egitim satisi sertifika
      // turu olmadan backend'de 400 alir, danisman nedenini anlamaz.
      setCertTypesError(true);
    }
  }, [authHeaders]);

  useEffect(() => { fetchCertTypes(); }, [fetchCertTypes]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return courses.filter((c) =>
      !q ||
      (c.title_tr || c.title || '').toLowerCase().includes(q) ||
      (c.category || '').toLowerCase().includes(q)
    );
  }, [courses, search]);

  const getTitle = (c) => (lang === 'tr' ? c.title_tr || c.title : c.title || c.title_tr) || c.course_id;

  // FAZ 1 düzeltme turu — madde 6 (küçükler): öğrenci bu kursa zaten
  // kayıtlıysa seçim anında uyar. `existingKurslar` sayfanın zaten çektiği
  // `detail.kurslar` verisidir — burada YENİ bir istek AÇILMAZ (talimat:
  // "veri yoksa yeni istek ekleme"). Uyarı bilgilendirmedir, seçimi
  // ENGELLEMEZ — danışman bilerek ikinci bir kayıt/satış açmak isteyebilir
  // (örn. önceki kayıt iptal/tamamlanmış olabilir).
  const isAlreadyEnrolled = (courseId) => (existingKurslar || []).some((k) => k.course_id === courseId);

  const handleAdd = (course, isPrimary) => {
    if (!isPrimary && course.meb_onayli) {
      toast.error(tr(
        'MEB onaylı kurs yalnızca ana eğitim olarak eklenebilir',
        'MEB-approved course can only be added as main education'
      ));
      return;
    }
    const exists = cartItems.some((c) => c.course_id === course.course_id && c.is_primary === isPrimary);
    if (exists) {
      toast.error(tr('Bu kurs zaten sepette', 'This course is already in the cart'));
      return;
    }
    if (isAlreadyEnrolled(course.course_id)) {
      toast.warning(t('sales.course_already_enrolled'));
    }
    setCartItems((prev) => [
      ...prev,
      {
        course_id: course.course_id,
        title: getTitle(course),
        catalog_price: course.price || 0,
        // Düzenlenebilir fiyat metni — varsayılan katalog fiyatı. Kullanıcı
        // dokunmazsa price_override HİÇ gönderilmez (bkz. coursesPayload).
        price_input: String(course.price || 0),
        is_primary: isPrimary,
      },
    ]);
  };

  const handleRemove = (index) => {
    setCartItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePriceInputChange = (index, value) => {
    setCartItems((prev) => prev.map((item, i) => (i === index ? { ...item, price_input: value } : item)));
  };

  // Fiyat girişi yalnızca katalog fiyatı > 0 olan (ücretsiz olmayan) satırlarda
  // gösterilir/doğrulanır — katalog fiyatı 0 olan kurslar "Ücretsiz" olarak kalır.
  const rowHasPriceInput = (item) => (item.catalog_price || 0) > 0;
  const rowPriceNumeric = (item) => Number(item.price_input);
  // EK (is_primary=false) satirlarda 0 GECERLIDIR — ek egitim ucretsiz
  // verilebiliyor (Recep karari, 24 Agustos 2026). ANA kursta alt sinir hala
  // 0'dan buyuk: dansimanin ana urunu kazayla bedavaya vermesini engelleyen
  // korkuluk bilerek duruyor.
  // Backend tarafi zaten hizali: kalem bazinda 0 kabul ediliyor (yalnizca
  // NEGATIF reddediliyor), yalnizca satisin TOPLAMI 0 olamaz
  // (sales_service.py "satis_tutari_sifir") — yani tamami bedava bir satis
  // bu esnetmeyle de olusturulamaz.
  const rowPriceMin = (item) => (item.is_primary ? 0 : -1); // n > min
  const rowPriceValid = (item) => {
    if (!rowHasPriceInput(item)) return true;
    const n = rowPriceNumeric(item);
    return typeof item.price_input === 'string' && item.price_input.trim() !== ''
      && Number.isFinite(n) && n > rowPriceMin(item);
  };
  const rowIsModified = (item) => {
    if (!rowHasPriceInput(item) || !rowPriceValid(item)) return false;
    return Math.round(rowPriceNumeric(item) * 100) !== Math.round(item.catalog_price * 100);
  };

  const hasPriceErrors = cartItems.some((item) => !rowPriceValid(item));

  // Katalog fiyatı DEĞİL — girilen (varsayılan veya danışmanın değiştirdiği)
  // fiyatların toplamı. Onay diyaloğu ve özet bu değeri gösterir.
  const cartTotal = useMemo(
    () =>
      cartItems.reduce((sum, item) => {
        if (!rowHasPriceInput(item)) return sum;
        if (!rowPriceValid(item)) return sum;
        return sum + rowPriceNumeric(item);
      }, 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cartItems]
  );

  // Sepette ANA egitim varsa sertifika turu zorunlu. Sepet tamamen EK ise
  // (ucretsiz EK kaydi dahil) istenmiyor — backend kurali da boyle.
  const anaEgitimVar = cartItems.some((c) => c.is_primary);
  const sertifikaEksik = anaEgitimVar && !certificateTypeId;
  // FAZ 1 karma ödeme turu (2026-09-25, madde 3), düzeltme 2026-09-28 —
  // kutu işaretliyse (certificateFeeIncluded=true) kurs fiyatı sertifikayı
  // ZATEN içeriyor demektir, ücret toplama EKLENMEZ. Kutu işaretsizse ücret
  // toplama eklenir. Backend `certificate_type_id` biliniyorsa ücreti
  // KENDİSİ db.certificate_types'tan okur (bkz. resolve_certificate_fee_amount)
  // — buradaki hesap yalnızca ARAYÜZDE gösterilen/PaymentPlanFields'e geçen
  // toplamı doğru tutmak için.
  const selectedCertFeeAmount = useMemo(() => {
    if (!certificateTypeId) return 0;
    const ct = certTypes.find((c) => c.cert_id === certificateTypeId);
    return Number(ct?.exam_fee || 0);
  }, [certificateTypeId, certTypes]);
  const certFee = certificateFeeIncluded ? 0 : selectedCertFeeAmount;
  const saleTotal = cartTotal + certFee;

  const canSubmit = cartItems.length > 0 && !hasPriceErrors
    && !sertifikaEksik
    && paymentPlanValid
    && !submitting;

  const courseNames = cartItems.map((c) => c.title).join(', ');
  const confirmDescription = tr(
    `${courseNames} için ${fmtMoney(saleTotal)} tutarında kayıt oluşturulacak. Bu işlem satış kaydı oluşturur.`,
    `An enrollment of ${fmtMoney(saleTotal)} will be created for ${courseNames}. This creates a sale record.`
  );

  const handleConfirmedSubmit = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    try {
      const coursesPayload = cartItems.map((item) => {
        const payloadItem = { course_id: item.course_id, is_primary: item.is_primary };
        // certificate_type_id YALNIZCA ANA kaleme eklenir. Backend zaten ilk
        // is_primary kalemi ANA kabul edip digerlerini EK'e dusuruyor; her
        // kaleme yazmak, EK satirlarina anlamsiz bir sertifika turu islerdi.
        if (item.is_primary && certificateTypeId) {
          payloadItem.certificate_type_id = certificateTypeId;
          // FAZ 1 karma ödeme turu (2026-09-25, madde 3), düzeltme 2026-09-28
          // — backend sözleşmesi: kalemde `certificate_fee_included: true` =
          // "sunucu sertifika ücretini borca ekler". Arayüz kutusu bunun
          // TERSİ anlamı taşır (işaretli = fiyat sertifikayı zaten içerir,
          // eklenmez), bu yüzden burada bilerek TERS çevrilir. Ücret
          // İSTEMCİDEN değil sunucudaki kayıttan (db.certificate_types.exam_fee)
          // okunur (bkz. resolve_certificate_fee_amount); burada yalnızca
          // "ekle" bayrağı gönderilir.
          if (!certificateFeeIncluded) {
            payloadItem.certificate_fee_included = true;
          }
        }
        // Yalnızca kullanıcı katalog fiyatından FARKLI bir değer girdiyse
        // price_override gönder — dokunulmadıysa alan hiç gitmez, backend
        // kursun tanımlı fiyatını uygular.
        if (rowIsModified(item)) {
          payloadItem.price_override = rowPriceNumeric(item);
        }
        return payloadItem;
      });
      const body = {
        courses: coursesPayload,
        // FAZ 1 karma ödeme turu (2026-09-25) — Peşinat + Kalan alanları
        // (down_payment, down_payment_method/bank_id, installments/
        // first_due_date VEYA installment_plan, payment_method)
        // PaymentPlanFields tarafından hesaplanır (bkz. yukarıdaki onChange).
        ...paymentPlanFields,
        ...(notes.trim() ? { payment_notes: notes.trim() } : {}),
      };
      const r = await axios.post(`${API}/admin/students/${userId}/enroll-courses`, body, { headers: authHeaders() });
      const skipped = r.data?.skipped_courses || [];
      const enrolled = r.data?.enrolled_courses || [];
      if (skipped.length > 0) {
        // Backend skipped_courses içine İKİ FARKLI reason yazıyor
        // (bkz. routes/admin.py::admin_enroll_student_courses):
        //   - "already_enrolled"  → öğrenci bu kursa zaten kayıtlı, hata değil.
        //   - "course_not_found"  → kurs katalogda bulunamadı; bu bir VERİ
        //     TUTARSIZLIĞI sinyali (liste bayat olabilir), "zaten kayıtlı"
        //     ile karıştırılırsa danışman yanlış teşhis koyar. Tanımadığımız
        //     bir reason gelirse de sessizce kaybetmeyip genel bir metinle
        //     gösteriyoruz — ileride backend yeni bir reason eklerse burası
        //     kör kalmasın.
        //
        // Ham course_id yerine kurs BAŞLIĞI göster; başlık cartItems'ta
        // bulunamazsa (beklenmeyen durum) course_id'ye geri düş.
        // setCartItems([])'DEN ÖNCE hesaplanmalı, yoksa başlıklar kaybolur.
        const namesOf = (group) => group
          .map((s) => cartItems.find((c) => c.course_id === s.course_id)?.title || s.course_id)
          .join(', ');
        const alreadyEnrolled = skipped.filter((s) => s.reason === 'already_enrolled');
        const notFound = skipped.filter((s) => s.reason === 'course_not_found');
        const unknown = skipped.filter((s) => s.reason !== 'already_enrolled' && s.reason !== 'course_not_found');

        const sentences = [];
        if (alreadyEnrolled.length > 0) {
          const names = namesOf(alreadyEnrolled);
          sentences.push(
            enrolled.length === 0
              ? tr(`Hiçbir kurs eklenemedi — zaten kayıtlı: ${names}`, `No courses were added — already enrolled: ${names}`)
              : tr(`Şu kurslar zaten kayıtlı olduğu için eklenmedi: ${names}`, `The following courses were not added because they are already enrolled: ${names}`)
          );
        }
        if (notFound.length > 0) {
          const names = namesOf(notFound);
          sentences.push(
            enrolled.length === 0
              ? tr(
                  `Hiçbir kurs eklenemedi — kurs bulunamadı: ${names}. Liste güncel olmayabilir, sayfayı yenileyip tekrar deneyin.`,
                  `No courses were added — course not found: ${names}. The list may be outdated — refresh the page and try again.`
                )
              : tr(
                  `Şu kurslar kataloğunda bulunamadığı için eklenmedi: ${names}. Liste güncel olmayabilir, sayfayı yenileyip tekrar deneyin.`,
                  `The following courses were not added because they could not be found in the catalog: ${names}. The list may be outdated — refresh the page and try again.`
                )
          );
        }
        if (unknown.length > 0) {
          const names = namesOf(unknown);
          sentences.push(
            enrolled.length === 0
              ? tr(`Hiçbir kurs eklenemedi: ${names}`, `No courses were added: ${names}`)
              : tr(`Şu kurslar eklenemedi: ${names}`, `The following courses were not added: ${names}`)
          );
        }

        // Tek toast'ta birden fazla cümle: hem already_enrolled hem
        // course_not_found aynı anda dönerse kullanıcıyı toast yağmuruna
        // tutmamak için tek toast içinde birleştiriyoruz. Şiddet: hiçbir şey
        // eklenmediyse error, kısmi eklendiyse warning — course_not_found
        // veri tutarsızlığı sinyali olduğu için bu warning'in altına
        // düşürülmüyor (zaten en az warning veriyoruz).
        const message = sentences.join(' ');
        if (enrolled.length === 0) {
          toast.error(message);
        } else {
          toast.warning(message);
        }
      }
      if (enrolled.length > 0) {
        toast.success(tr('Satış kaydedildi.', 'Sale recorded.'));
        setCartItems([]);
        setNotes('');
        setCertificateTypeId('');
        setCertificateFeeIncluded(true);
        setPaymentPlanFields({});
        setPaymentPlanValid(false);
        // PaymentPlanFields tamamen kendi state'ini yönetir (down_payment,
        // remainingMethod, InstallmentPreview vb.) — bir sonraki satış için
        // sıfırdan başlaması için `key` değiştirilip bileşen REMOUNT edilir.
        setPaymentPlanResetKey((k) => k + 1);
        onSuccess?.();
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardTitle icon={<ShoppingCart size={16} style={{ color: 'var(--crm-navy)' }} />}>
        {tr('Ders Ekle / Satış', 'Add Course / Sale')}
      </CardTitle>

      {coursesLoading && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-9 w-full" />)}
        </div>
      )}

      {!coursesLoading && coursesError && <ErrorRetry onRetry={fetchCourses} tr={tr} />}

      {!coursesLoading && !coursesError && (
        <div className="space-y-4">
          {/* Kurs arama */}
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tr('Kurs ara...', 'Search courses...')}
              className={INPUT_CLS + ' pl-9'}
            />
          </div>

          {/* Kurs listesi */}
          <div className="border-2 border-[var(--border-light)] rounded-xl overflow-hidden">
            {filtered.length === 0 ? (
              <p className="p-4 text-center text-sm text-gray-400">
                {tr('Kurs bulunamadı', 'No courses found')}
              </p>
            ) : (
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b-2 border-[var(--border-light)] sticky top-0 z-10">
                    <tr>
                      <th scope="col" className="text-left px-3 py-2 text-[11px] font-bold text-[var(--text-muted)] uppercase">
                        {tr('Kurs', 'Course')}
                      </th>
                      <th scope="col" className="text-right px-2 py-2 text-[11px] font-bold text-[var(--text-muted)] uppercase w-24">
                        {tr('Fiyat', 'Price')}
                      </th>
                      <th scope="col" className="text-center px-2 py-2 text-[11px] font-bold text-[var(--text-muted)] uppercase w-20">
                        {tr('+ Ana', '+ Main')}
                      </th>
                      <th scope="col" className="text-center px-2 py-2 text-[11px] font-bold text-[var(--text-muted)] uppercase w-20">
                        {tr('+ Ek', '+ Extra')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((c, idx) => {
                      const isMeb = !!c.meb_onayli;
                      const alreadyMain = cartItems.some((ci) => ci.course_id === c.course_id && ci.is_primary);
                      const alreadyExtra = cartItems.some((ci) => ci.course_id === c.course_id && !ci.is_primary);
                      const isEven = idx % 2 === 0;
                      return (
                        <tr key={c.course_id} className={`border-t border-[var(--border-light)] ${isEven ? 'bg-white' : 'bg-gray-50/60'}`}>
                          <td className="px-3 py-1.5">
                            <p className="font-bold text-xs truncate max-w-[200px]">{getTitle(c)}</p>
                            <p className="text-[10px] text-[var(--text-muted)]">{c.category}</p>
                          </td>
                          <td className="px-2 py-1.5 text-right font-bold text-xs text-gray-700">
                            {c.price ? fmtMoney(c.price) : tr('Ücretsiz', 'Free')}
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            {alreadyMain ? (
                              <CheckCircle2 size={14} className="text-emerald-500 mx-auto" aria-label={tr('Eklendi', 'Added')} />
                            ) : (
                              <motion.button
                                type="button"
                                whileHover={{ scale: 1.15 }}
                                whileTap={{ scale: 0.85 }}
                                onClick={() => handleAdd(c, true)}
                                aria-label={tr('Ana eğitim olarak ekle', 'Add as main course')}
                                className="inline-flex items-center gap-0.5 px-2 py-1 rounded-lg bg-cyan-100 hover:bg-cyan-200 text-cyan-700 text-[11px] font-bold"
                              >
                                <Plus size={11} />{tr('Ana', 'Main')}
                              </motion.button>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-center">
                            {isMeb ? (
                              <span
                                className="inline-flex items-center gap-0.5 px-2 py-1 rounded-lg bg-gray-100 text-gray-300 text-[11px] font-bold cursor-not-allowed"
                                title={tr('MEB onaylı — yalnızca ana eğitim', 'MEB-approved — main only')}
                              >
                                <Plus size={11} />{tr('Ek', 'Extra')}
                              </span>
                            ) : alreadyExtra ? (
                              <CheckCircle2 size={14} className="text-emerald-500 mx-auto" aria-label={tr('Eklendi', 'Added')} />
                            ) : (
                              <motion.button
                                type="button"
                                whileHover={{ scale: 1.15 }}
                                whileTap={{ scale: 0.85 }}
                                onClick={() => handleAdd(c, false)}
                                aria-label={tr('Ek eğitim olarak ekle', 'Add as extra course')}
                                className="inline-flex items-center gap-0.5 px-2 py-1 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-700 text-[11px] font-bold"
                              >
                                <Plus size={11} />{tr('Ek', 'Extra')}
                              </motion.button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Sepet */}
          <div>
            <p className="text-[11px] font-bold text-[var(--text-muted)] uppercase tracking-wide mb-2">
              {tr('Seçilen Eğitimler', 'Selected Courses')} ({cartItems.length})
            </p>
            {cartItems.length === 0 ? (
              <div className="border-2 border-dashed border-[var(--border-light)] rounded-xl p-4 text-center text-sm text-gray-400">
                {tr('Henüz eğitim seçilmedi', 'No courses selected yet')}
              </div>
            ) : (
              <div className="space-y-1.5">
                {cartItems.map((item, i) => {
                  const hasPriceInput = rowHasPriceInput(item);
                  const priceValid = rowPriceValid(item);
                  const isModified = rowIsModified(item);
                  const rowKey = `${item.course_id}-${item.is_primary}`;
                  const inputId = `sepet-fiyat-${rowKey}-${i}`;
                  const errorId = `${inputId}-hata`;
                  return (
                    <div
                      key={rowKey}
                      className="flex items-center gap-2.5 px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white"
                    >
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold flex-shrink-0 ${item.is_primary ? 'bg-cyan-100 text-cyan-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {item.is_primary ? tr('ANA', 'MAIN') : tr('EK', 'EXTRA')}
                      </span>
                      <span className="flex-1 text-xs font-bold truncate">{item.title}</span>
                      {hasPriceInput ? (
                        <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                          <label htmlFor={inputId} className="sr-only">
                            {tr(`${item.title} fiyatı`, `${item.title} price`)}
                          </label>
                          <input
                            id={inputId}
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            min="0"
                            value={item.price_input}
                            onChange={(e) => handlePriceInputChange(i, e.target.value)}
                            aria-invalid={!priceValid}
                            aria-describedby={!priceValid ? errorId : undefined}
                            className={`w-24 text-right text-xs font-bold rounded-lg border-2 px-2 py-1 bg-white focus:outline-none ${
                              priceValid ? 'border-[var(--border-light)] text-gray-700' : 'border-rose-400 text-rose-600'
                            }`}
                          />
                          <span className="text-[9px] font-semibold text-gray-400">
                            {tr('Liste fiyatı', 'List price')}: {fmtMoney(item.catalog_price)}
                          </span>
                          {isModified && (
                            <span className="text-[9px] font-bold flex items-center gap-0.5" style={{ color: 'var(--crm-gold-dark)' }}>
                              <Pencil size={9} /> {tr('Değiştirildi', 'Modified')}
                            </span>
                          )}
                          {!priceValid && (
                            <span id={errorId} className="text-[9px] font-bold text-rose-600 flex items-center gap-0.5 max-w-[110px] text-right">
                              <AlertCircle size={9} className="flex-shrink-0" />
                              {item.is_primary
                                ? tr('0\'dan büyük geçerli bir sayı girin', 'Enter a valid number greater than 0')
                                : tr('0 veya daha büyük geçerli bir sayı girin', 'Enter a valid number of 0 or more')}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs font-bold text-gray-500 flex-shrink-0">{tr('Ücretsiz', 'Free')}</span>
                      )}
                      <motion.button
                        type="button"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => handleRemove(i)}
                        aria-label={tr('Sepetten çıkar', 'Remove from cart')}
                        className="p-1 rounded-lg hover:bg-rose-100 text-gray-400 hover:text-rose-600 flex-shrink-0"
                      >
                        <Trash2 size={13} />
                      </motion.button>
                    </div>
                  );
                })}
              </div>
            )}
            {/* Sertifika turu — YALNIZCA sepette ANA egitim varken gorunur.
                Sepet tamamen EK ise (ucretsiz EK kaydi dahil) istenmez;
                backend kurali da ayni (validate_certificate_type_or_400). */}
            {anaEgitimVar && (
              <div className="mt-3">
                <Field
                  label={t('sales.certificate_type_label')}
                  required
                  error={
                    certTypesError
                      ? tr('Sertifika türleri yüklenemedi. Sayfayı yenileyin.', 'Certificate types could not be loaded. Please refresh.')
                      : (sertifikaEksik ? tr('Ana eğitim için zorunludur', 'Required for the main course') : undefined)
                  }
                >
                  <select
                    value={certificateTypeId}
                    onChange={(e) => setCertificateTypeId(e.target.value)}
                    className={INPUT_CLS}
                    aria-label={t('sales.certificate_type_label')}
                  >
                    <option value="">{tr('Seçiniz...', 'Select...')}</option>
                    {certTypes.map((ct) => (
                      <option key={ct.cert_id} value={ct.cert_id}>
                        {(lang === 'en' ? (ct.name_en || ct.name) : ct.name)} — {fmtMoney(ct.exam_fee || 0)}
                      </option>
                    ))}
                  </select>
                </Field>
                {certificateTypeId && (
                  <>
                    <label className="mt-2 flex items-center gap-2 text-xs font-semibold" style={{ color: 'var(--crm-navy-text)' }}>
                      <input
                        type="checkbox"
                        checked={certificateFeeIncluded}
                        onChange={(e) => setCertificateFeeIncluded(e.target.checked)}
                        data-testid="ders-ekle-certificate-fee-included"
                      />
                      {t('sales.certificate_fee_included_label')}
                    </label>
                    {!certificateFeeIncluded && selectedCertFeeAmount > 0 && (
                      <p className="text-xs mt-1" style={{ color: 'var(--crm-muted)' }}>
                        {tr(
                          `+${fmtMoney(selectedCertFeeAmount)} eklenecek`,
                          `+${fmtMoney(selectedCertFeeAmount)} will be added`
                        )}
                      </p>
                    )}
                  </>
                )}
                {/* Liste bos ama hata da yoksa: katalogda hic sertifika turu
                    tanimlanmamis demektir (beklenmez, backend varsayilan 4
                    turu seed eder) — danisman burada tikanir ve nedenini
                    bilemez, acikca soyluyoruz. */}
                {!certTypesError && certTypes.length === 0 && (
                  <p className="text-xs mt-1" style={{ color: 'var(--crm-muted)' }}>
                    {tr(
                      'Hiç sertifika türü tanımlı değil. Yönetici eklemeli.',
                      'No certificate types are defined. An admin must add them.'
                    )}
                  </p>
                )}
              </div>
            )}
            {cartItems.length > 0 && (
              <div className="flex justify-between text-sm font-bold mt-2 px-1">
                <span style={{ color: 'var(--crm-muted)' }}>{tr('Toplam Tutar', 'Total Amount')}</span>
                <span style={{ color: 'var(--crm-navy-text)' }}>{fmtMoney(saleTotal)}</span>
              </div>
            )}
          </div>

          {/* Ödeme — FAZ 1 karma ödeme turu (2026-09-25): Peşinat + Kalan,
              bkz. components/sales/PaymentPlanFields.jsx dosya başı yorumu. */}
          <div className="pt-2 border-t" style={{ borderColor: 'var(--crm-card-border)' }}>
            <p className="text-[11px] font-bold uppercase tracking-wide mb-2 flex items-center gap-1.5" style={{ color: 'var(--crm-muted)' }}>
              <CreditCard size={13} /> {tr('Ödeme', 'Payment')}
            </p>
            <PaymentPlanFields
              key={paymentPlanResetKey}
              role={isAdmin ? 'admin' : 'danisman'}
              totalAmount={saleTotal}
              banks={collectionBanks}
              banksLoading={banksLoading}
              disabled={submitting}
              onChange={(fields, meta) => { setPaymentPlanFields(fields); setPaymentPlanValid(meta.isValid); }}
            />

            <div className="mt-3">
              <Field label={tr('Not (opsiyonel)', 'Note (optional)')}>
                <textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className={INPUT_CLS + ' resize-none'}
                  placeholder={tr('İsteğe bağlı not...', 'Optional note...')}
                />
              </Field>
            </div>
          </div>

          {/* Gönder */}
          <motion.button
            type="button"
            whileHover={canSubmit ? { scale: 1.02 } : {}}
            whileTap={canSubmit ? { scale: 0.98 } : {}}
            onClick={() => setConfirmOpen(true)}
            disabled={!canSubmit}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-500 text-white text-sm font-bold border-b-4 border-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <><Loader2 size={16} className="animate-spin" /> {tr('Kaydediliyor...', 'Saving...')}</>
            ) : (
              <><CheckCircle2 size={16} /> {tr('Satışı Kaydet', 'Save Sale')}</>
            )}
          </motion.button>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(v) => !submitting && setConfirmOpen(v)}
        title={tr('Satışı Onayla', 'Confirm Sale')}
        description={confirmDescription}
        variant="info"
        confirmLabel={tr('Onayla ve Kaydet', 'Confirm & Save')}
        onConfirm={handleConfirmedSubmit}
        loading={submitting}
      />
    </Card>
  );
}

// ─── Bölüm 4 — Satışlar ───────────────────────────────────────────────────────

function SatislarKart({ satislar, tr }) {
  return (
    <Card>
      <CardTitle icon={<ReceiptText size={16} style={{ color: 'var(--crm-navy)' }} />}>
        {tr('Satışlar', 'Sales')}
      </CardTitle>
      {satislar.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--crm-muted)' }}>
          {tr('Henüz satış kaydı yok.', 'No sales recorded yet.')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b" style={{ borderColor: 'var(--crm-card-border)' }}>
                <th scope="col" className="text-left py-2 pr-2 font-bold text-xs uppercase" style={{ color: 'var(--crm-muted-2)' }}>
                  {tr('Tarih', 'Date')}
                </th>
                <th scope="col" className="text-right py-2 pr-2 font-bold text-xs uppercase" style={{ color: 'var(--crm-muted-2)' }}>
                  {tr('Tutar', 'Amount')}
                </th>
                <th scope="col" className="text-left py-2 pr-2 font-bold text-xs uppercase" style={{ color: 'var(--crm-muted-2)' }}>
                  {tr('Durum', 'Status')}
                </th>
                <th scope="col" className="text-center py-2 pr-2 font-bold text-xs uppercase" style={{ color: 'var(--crm-muted-2)' }}>
                  {tr('Taksit', 'Installments')}
                </th>
                <th scope="col" className="text-right py-2 pr-2 font-bold text-xs uppercase" style={{ color: 'var(--crm-muted-2)' }}>
                  {tr('Ödenen / Kalan', 'Paid / Remaining')}
                </th>
                <th scope="col" className="text-left py-2 font-bold text-xs uppercase" style={{ color: 'var(--crm-muted-2)' }}>
                  {tr('Satışı Yapan', 'Sold By')}
                </th>
              </tr>
            </thead>
            <tbody>
              {satislar.map((s) => (
                <tr key={s.invoice_id} className="border-b last:border-0" style={{ borderColor: 'var(--crm-card-border)' }}>
                  <td className="py-2 pr-2">{fmtDate(s.tarih)}</td>
                  <td className="py-2 pr-2 text-right font-bold">{fmtMoney(s.tutar)}</td>
                  <td className="py-2 pr-2"><SatisDurumBadge durum={s.durum} tr={tr} /></td>
                  <td className="py-2 pr-2 text-center">{s.taksit_sayisi ?? 0}</td>
                  <td className="py-2 pr-2 text-right whitespace-nowrap">
                    <span className="text-emerald-600 font-semibold">{fmtMoney(s.odenen)}</span>
                    {' / '}
                    <span className="text-rose-600 font-semibold">{fmtMoney(s.kalan)}</span>
                  </td>
                  <td className="py-2">{s.satis_personel_adi || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ─── Bölüm 5 — Görüşmeler (son 50) ────────────────────────────────────────────

function GorusmelerKart({ gorusmeler, tr }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <Card>
      <CardTitle icon={<MessageSquare size={16} style={{ color: 'var(--crm-navy)' }} />}>
        {tr('Görüşmeler', 'Calls')}
      </CardTitle>
      {gorusmeler.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--crm-muted)' }}>
          {tr('Henüz görüşme kaydı yok.', 'No call records yet.')}
        </p>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {gorusmeler.map((g, i) => {
            const id = g.gorusme_id || i;
            const notText = g.not || '';
            const needsClamp = notText.length > NOTE_CLAMP_THRESHOLD;
            const isExpanded = !!expanded[id];
            return (
              <div key={id} className="p-2.5 rounded-lg" style={{ backgroundColor: 'var(--crm-cream)' }}>
                <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                  <span className="font-bold text-sm" style={{ color: 'var(--crm-navy-text)' }}>
                    {g.konu || tr('Konu belirtilmemiş', 'No topic')}
                  </span>
                  <span className="text-[11px] font-semibold" style={{ color: 'var(--crm-muted)' }}>
                    {fmtDateTime(g.tarih)}
                  </span>
                </div>
                {notText && (
                  <>
                    <p
                      className={`text-xs whitespace-pre-wrap ${!isExpanded && needsClamp ? 'line-clamp-3' : ''}`}
                      style={{ color: 'var(--crm-navy-text)' }}
                    >
                      {notText}
                    </p>
                    {needsClamp && (
                      <button
                        onClick={() => toggle(id)}
                        className="text-[11px] font-bold mt-1 flex items-center gap-0.5"
                        style={{ color: 'var(--crm-info)' }}
                      >
                        {isExpanded ? tr('Daha az göster', 'Show less') : tr('Devamını gör', 'Show more')}
                        {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                      </button>
                    )}
                  </>
                )}
                <p className="text-[11px] font-semibold mt-1" style={{ color: 'var(--crm-muted-2)' }}>
                  {tr('Oluşturan', 'Created by')}: {g.olusturan_adi || '-'}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ─── Ana sayfa ────────────────────────────────────────────────────────────────

export default function DanismanOgrenciDetay() {
  const { userId } = useParams();
  const { lang } = useLanguage();
  const { authHeaders, user } = useAuth();
  const toast = useToast();
  const tr = (t, e) => (lang === 'tr' ? t : e);
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorCode, setErrorCode] = useState(null);

  const backHref = '/admin/cockpit?tab=ogrencilerim';

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(false);
    setErrorCode(null);
    try {
      const r = await axios.get(`${API}/danisman/ogrencilerim/${userId}`, { headers: authHeaders() });
      setDetail(r.data);
    } catch (err) {
      setError(true);
      const code = getApiErrorCode(err);
      setErrorCode(code);
      if (code !== 'yetkisiz_ogrenci') {
        toast.error(getApiErrorMessage(err, lang));
      }
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, lang]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const ogrenciAdi = detail?.ogrenci?.ad_soyad || userId;

  if (loading) {
    return (
      <AdminLayout>
        <div className="-m-4 lg:-m-6 p-4 lg:p-6 min-h-full" style={{ backgroundColor: 'var(--crm-cream)' }}>
          <div className="space-y-3 max-w-3xl">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error && errorCode === 'yetkisiz_ogrenci') {
    return (
      <AdminLayout>
        <div
          className="-m-4 lg:-m-6 p-4 lg:p-6 min-h-full flex items-center justify-center"
          style={{ backgroundColor: 'var(--crm-cream)' }}
        >
          <div className="bg-white rounded-2xl p-8 max-w-md text-center space-y-4" style={{ border: '1px solid var(--crm-card-border)' }}>
            <ShieldAlert size={36} className="mx-auto" style={{ color: 'var(--crm-danger)' }} />
            <p className="text-sm font-semibold" style={{ color: 'var(--crm-navy-text)' }}>
              {tr('Bu öğrenci size atanmamış.', 'This student is not assigned to you.')}
            </p>
            <Link
              to={backHref}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white"
              style={{ backgroundColor: 'var(--crm-navy)' }}
            >
              {tr("Öğrencilerim'e Dön", 'Back to My Students')}
            </Link>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !detail) {
    return (
      <AdminLayout>
        <div className="-m-4 lg:-m-6 p-4 lg:p-6 min-h-full" style={{ backgroundColor: 'var(--crm-cream)' }}>
          <div className="max-w-xl">
            <ErrorRetry onRetry={fetchDetail} tr={tr} />
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="-m-4 lg:-m-6 p-4 lg:p-6 min-h-full" style={{ backgroundColor: 'var(--crm-cream)' }}>
        <PageHeader
          breadcrumbs={[
            rootBreadcrumb(user?.role, tr),
            { label: tr('Öğrencilerim', 'My Students'), href: backHref },
            { label: ogrenciAdi },
          ]}
          title={ogrenciAdi}
          subtitle={tr('Öğrenci detayı — danışman görünümü', 'Student detail — advisor view')}
          actions={
            isAdmin ? (
              <Link
                to={`/admin/students/${userId}`}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl border-2 text-sm font-bold hover:bg-gray-50"
                style={{ borderColor: 'var(--crm-card-border)', color: 'var(--crm-navy-text)' }}
              >
                <ExternalLink size={14} />
                {tr('Tam yönetici görünümü', 'Full admin view')}
              </Link>
            ) : undefined
          }
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ErrorBoundary fallback={(_err, retry) => <BlockErrorFallback onRetry={retry} tr={tr} />}>
            <OgrenciBilgileriKart ogrenci={detail.ogrenci} tr={tr} />
          </ErrorBoundary>
          <ErrorBoundary fallback={(_err, retry) => <BlockErrorFallback onRetry={retry} tr={tr} />}>
            <KurslarKart kurslar={detail.kurslar || []} tr={tr} />
          </ErrorBoundary>
        </div>

        <div className="mt-4">
          <ErrorBoundary fallback={(_err, retry) => <BlockErrorFallback onRetry={retry} tr={tr} />}>
            <DersEkleKart
              userId={userId}
              authHeaders={authHeaders}
              lang={lang}
              tr={tr}
              onSuccess={fetchDetail}
              isAdmin={isAdmin}
              existingKurslar={detail.kurslar || []}
            />
          </ErrorBoundary>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <ErrorBoundary fallback={(_err, retry) => <BlockErrorFallback onRetry={retry} tr={tr} />}>
            <SatislarKart satislar={detail.satislar || []} tr={tr} />
          </ErrorBoundary>
          <ErrorBoundary fallback={(_err, retry) => <BlockErrorFallback onRetry={retry} tr={tr} />}>
            <GorusmelerKart gorusmeler={detail.gorusmeler || []} tr={tr} />
          </ErrorBoundary>
        </div>
      </div>
    </AdminLayout>
  );
}
